import React, { useCallback, useEffect, useState } from 'react';
import { Table, Card, Image, DatePicker } from 'antd';
import { Link } from 'react-router-dom';
import {
  getShopCashSettlementList,
  financialManagementAccountList,
} from '../../../apis/fms';
import {
  FmsAccountManagement,
  FmsPaymentDetailItem,
  FmsShopCashSettlement,
} from '../../../types/fms';
import PrivateReceiptImage from 'components/private-receipt-image';
import { useAppSelector } from '../../../store/hooks';
import { selectUserInfo } from '../../../store/slices/userInfoSlice';
import { useTranslation } from 'react-i18next';
import LOCALS from '../../../commons/locals';
import dayjs, { Dayjs } from 'dayjs';
import {
  formatCashSettlementAmount,
  getCashSettlementCurrencyByShop,
  getCashSettlementCurrencySymbol,
} from './cash-settlement-currency';
import {
  CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME,
  CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS,
  CASH_SETTLEMENT_HISTORY_DETAIL_SCROLL_X,
  CASH_SETTLEMENT_HISTORY_HEADER_CLASS_NAME,
  CASH_SETTLEMENT_HISTORY_TABLE_SCROLL_X,
  CASH_SETTLEMENT_HISTORY_TEXT_CELL_CLASS_NAME,
} from './cash-settlement-history-layout';
import {
  buildRecycleStatRows,
  hasSettlementDetails,
} from './cash-settlement-history-utils';

interface CashSettlementHistoryProps {
  onClose?: () => void;
  shopId?: number;
}

const CashSettlementHistory: React.FC<CashSettlementHistoryProps> = ({
  onClose,
  shopId,
}) => {
  const { t } = useTranslation();
  const userInfo = useAppSelector(selectUserInfo);
  const selectedShopId = shopId ?? userInfo?.shop;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FmsShopCashSettlement[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [settlementDateRange, setSettlementDateRange] = useState<
    [Dayjs, Dayjs] | null
  >(null);
  const [paymentAccountList, setPaymentAccountList] = useState<
    FmsAccountManagement[]
  >([]);

  const currencyType = getCashSettlementCurrencyByShop(selectedShopId);
  const currencySymbol = getCashSettlementCurrencySymbol(currencyType);

  // 获取结算列表数据
  const fetchData = useCallback(async () => {
    if (!selectedShopId) return;

    try {
      setLoading(true);
      const response = await getShopCashSettlementList({
        shopId: selectedShopId,
        pageNum,
        pageSize,
        settlementStartDate: settlementDateRange?.[0].format('YYYY-MM-DD'),
        settlementEndDate: settlementDateRange?.[1].format('YYYY-MM-DD'),
      });

      setData(response.list || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Failed to fetch cash settlement list:', error);
    } finally {
      setLoading(false);
    }
  }, [pageNum, pageSize, selectedShopId, settlementDateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 支付账户列表，用于将快照中的 payType（accountCode）映射为账户名称
  useEffect(() => {
    if (!selectedShopId) return;

    financialManagementAccountList(selectedShopId)
      .then((list) => setPaymentAccountList(list ?? []))
      .catch((error) => {
        console.error('Failed to fetch payment account list:', error);
      });
  }, [selectedShopId]);

  // 格式化金额显示
  const formatAmount = (amount: number | null | undefined): string =>
    formatCashSettlementAmount(amount, currencyType);

  // 格式化日期时间
  const formatDateTime = (dateTime: string): string => {
    return dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss');
  };

  // 格式化日期
  const formatDate = (date: string): string => {
    return dayjs(date).format('YYYY-MM-DD');
  };

  const renderHeader = (label: string) => (
    <span className={CASH_SETTLEMENT_HISTORY_HEADER_CLASS_NAME}>{label}</span>
  );

  // 支付明细快照中的单组清单（现金 / 非现金）
  const renderPaymentDetailTable = (
    payments: FmsPaymentDetailItem[],
    titleKey: string,
    totalKey: string,
  ) => {
    const totalAmount = payments.reduce(
      (sum, payment) => sum + Number(payment.payAmount),
      0,
    );

    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          {t(titleKey)}
        </h4>
        <Table
          columns={[
            {
              title: renderHeader(t(LOCALS.PVfpzNPNmJ)),
              dataIndex: 'orderId',
              key: 'orderId',
              width: 140,
              render: (orderId: string | number) => (
                <Link
                  to={`/oms/order-view/${orderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={CASH_SETTLEMENT_HISTORY_TEXT_CELL_CLASS_NAME}
                >
                  #{orderId}
                </Link>
              ),
            },
            {
              title: renderHeader(t(LOCALS.sales_product_id)),
              dataIndex: 'productIds',
              key: 'productIds',
              width: 140,
              render: (productIds?: string | null) => (
                <span className={CASH_SETTLEMENT_HISTORY_TEXT_CELL_CLASS_NAME}>
                  {productIds || '-'}
                </span>
              ),
            },
            {
              title: renderHeader(t(LOCALS.QIXxrHAzgP)),
              dataIndex: 'payType',
              key: 'payType',
              width: 200,
              render: (payType: string) =>
                paymentAccountList.find(
                  (account) => account.accountCode === payType,
                )?.accountName || payType,
            },
            {
              title: renderHeader(t(LOCALS.payment_receipt_images)),
              dataIndex: 'receiptKeys',
              key: 'receiptKeys',
              width: 260,
              render: (receiptKeys?: string[]) =>
                receiptKeys && receiptKeys.length > 0 ? (
                  <Image.PreviewGroup>
                    <div className="flex items-center gap-2">
                      {receiptKeys.map((imageKey) => (
                        <PrivateReceiptImage
                          key={imageKey}
                          imageKey={imageKey}
                        />
                      ))}
                    </div>
                  </Image.PreviewGroup>
                ) : (
                  '-'
                ),
            },
            {
              title: renderHeader(t(LOCALS.note)),
              dataIndex: 'note',
              key: 'note',
              width: 160,
              render: (note?: string | null) => (
                <span className="block whitespace-pre-wrap break-words text-gray-600 text-sm">
                  {note || '-'}
                </span>
              ),
            },
            {
              title: renderHeader(t(LOCALS.staff)),
              dataIndex: 'staffName',
              key: 'staffName',
              width: 120,
              render: (staffName?: string | null) => (
                <span className={CASH_SETTLEMENT_HISTORY_TEXT_CELL_CLASS_NAME}>
                  {staffName || '-'}
                </span>
              ),
            },
            {
              title: renderHeader(t(LOCALS.MZYosCkjLE)),
              dataIndex: 'payAmount',
              key: 'payAmount',
              width: 160,
              align: 'right' as const,
              render: (payAmount: string | number) => (
                <span
                  className={CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}
                >
                  {currencySymbol}{' '}
                  {formatAmount(Number(payAmount))}
                </span>
              ),
            },
          ]}
          dataSource={payments}
          rowKey={(payment, index) => `${payment.orderId}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: CASH_SETTLEMENT_HISTORY_DETAIL_SCROLL_X }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                <span className="font-medium">{t(totalKey)}</span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <span
                  className={`font-medium ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
                >
                  {currencySymbol}{' '}
                  {formatAmount(totalAmount)}
                </span>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </div>
    );
  };

  const renderRecycleStats = (record: FmsShopCashSettlement) => {
    const rows = buildRecycleStatRows(record.recycleStats ?? []);

    if (!rows.length) return null;

    return (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">
          {t(LOCALS.daily_register_close_recycle_order_daily_stats_title)}
        </h4>
        <div className="flex flex-wrap gap-3">
          {rows.map((row) => (
            <div
              key={row.key}
              className="bg-white rounded-lg border border-gray-200 px-4 py-3 min-w-[160px]"
            >
              <div className="text-xs font-medium text-gray-500 mb-2">
                {t(row.labelKey)}
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-gray-400">
                    {t(LOCALS.order_count)}
                  </span>
                  <span
                    className={`text-sm font-semibold ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
                  >
                    {t('daily_register_close_recycle_order_daily_count', {
                      count: row.count,
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-gray-400">
                    {t(LOCALS.total_amount)}
                  </span>
                  <span
                    className={`text-sm font-semibold ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
                  >
                    {row.currency} {formatAmount(row.totalAmount)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 展开行：结算时刻的支付构成、跨期退款和回收统计快照
  const renderSettlementDetails = (record: FmsShopCashSettlement) => {
    const { cashPayments, nonCashPayments, refundPayments } =
      record.paymentDetails ?? {};

    return (
      <div className="space-y-6 rounded-md bg-gray-50 px-4 py-3">
        {renderRecycleStats(record)}
        {!!cashPayments?.length &&
          renderPaymentDetailTable(
            cashPayments,
            LOCALS.daily_register_close_cash_payment_snapshot,
            LOCALS.daily_register_close_cash_payment_snapshot_total,
          )}
        {!!nonCashPayments?.length &&
          renderPaymentDetailTable(
            nonCashPayments,
            LOCALS.daily_register_close_cashless_payment_snapshot,
            LOCALS.daily_register_close_cashless_payment_snapshot_total,
          )}
        {!!refundPayments?.length &&
          renderPaymentDetailTable(
            refundPayments,
            LOCALS.daily_register_close_refund_payments,
            LOCALS.daily_register_close_refund_payments_total,
          )}
      </div>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: renderHeader(t(LOCALS.daily_register_close_date)),
      dataIndex: 'settlementDate',
      key: 'settlementDate',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.settlementDate,
      render: (date: string) => (
        <span
          className={`font-medium ${CASH_SETTLEMENT_HISTORY_TEXT_CELL_CLASS_NAME}`}
        >
          {formatDate(date)}
        </span>
      ),
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_opening_cash_amount)),
      dataIndex: 'beginningCashAmount',
      key: 'beginningCashAmount',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.beginningCashAmount,
      render: (amount: number) => (
        <span
          className={`text-gray-700 ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
        >
          {currencySymbol} {formatAmount(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_cash_in_from_safe)),
      dataIndex: 'treasuryCashInAmount',
      key: 'treasuryCashInAmount',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.treasuryCashInAmount,
      render: (amount: number) => (
        <span
          className={`text-green-600 ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
        >
          {currencySymbol} {formatAmount(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_cash_out_to_safe)),
      dataIndex: 'treasuryCashOutAmount',
      key: 'treasuryCashOutAmount',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.treasuryCashOutAmount,
      render: (amount: number) => (
        <span
          className={`text-red-600 ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
        >
          {currencySymbol} {formatAmount(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: renderHeader(
        t(LOCALS.daily_register_close_system_cash_net_amount),
      ),
      dataIndex: 'systemCashSales',
      key: 'systemCashSales',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.systemCashSales,
      render: (amount: number) => (
        <span
          className={`text-blue-600 ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
        >
          {currencySymbol} {formatAmount(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_expected_cash_amount)),
      dataIndex: 'expectedCash',
      key: 'expectedCash',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.expectedCash,
      render: (amount: number) => (
        <span
          className={`text-blue-700 font-medium ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
        >
          {currencySymbol} {formatAmount(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_counted_cash_amount)),
      dataIndex: 'verifiedCashAmount',
      key: 'verifiedCashAmount',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.verifiedCashAmount,
      render: (amount: number) => (
        <span
          className={`text-gray-900 font-medium ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
        >
          {currencySymbol} {formatAmount(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_cashless_sales_amount)),
      dataIndex: 'nonCashTotal',
      key: 'nonCashTotal',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.nonCashTotal,
      render: (amount: number) => (
        <span
          className={`text-purple-600 ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
        >
          {currencySymbol} {formatAmount(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_over_short_amount)),
      dataIndex: 'varianceAmount',
      key: 'varianceAmount',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.varianceAmount,
      render: (amount: number) => {
        if (amount === 0) {
          return (
            <span
              className={`text-green-600 font-medium ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
            >
              {currencySymbol} 0
            </span>
          );
        } else if (amount < 0) {
          return (
            <span
              className={`text-red-600 font-medium ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
            >
              {currencySymbol} {formatAmount(amount)}
            </span>
          );
        } else {
          return (
            <span
              className={`text-orange-600 font-medium ${CASH_SETTLEMENT_HISTORY_AMOUNT_CELL_CLASS_NAME}`}
            >
              {currencySymbol} {formatAmount(amount)}
            </span>
          );
        }
      },
      align: 'right' as const,
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_over_short_note_table)),
      dataIndex: 'note',
      key: 'note',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.note,
      render: (note: string) => (
        <span className="block whitespace-pre-wrap break-words text-gray-600 text-sm">
          {note || '-'}
        </span>
      ),
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_counted_by_name)),
      dataIndex: 'cashCounter',
      key: 'cashCounter',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.cashCounter,
      render: (counter: string) => (
        <span
          className={`text-gray-700 ${CASH_SETTLEMENT_HISTORY_TEXT_CELL_CLASS_NAME}`}
        >
          {counter}
        </span>
      ),
    },
    {
      title: renderHeader(t(LOCALS.daily_register_close_created_time)),
      dataIndex: 'createTime',
      key: 'createTime',
      width: CASH_SETTLEMENT_HISTORY_COLUMN_WIDTHS.createTime,
      render: (time: string) => (
        <span
          className={`text-gray-600 text-sm ${CASH_SETTLEMENT_HISTORY_TEXT_CELL_CLASS_NAME}`}
        >
          {formatDateTime(time)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* 数据表格 */}
      <Card>
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm text-gray-700">
            {t(LOCALS.daily_register_close_date)}
          </span>
          <DatePicker.RangePicker
            value={settlementDateRange}
            onChange={(dates) => {
              setSettlementDateRange(
                dates?.[0] && dates[1] ? [dates[0], dates[1]] : null,
              );
              setPageNum(1);
            }}
          />
        </div>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          expandable={{
            columnWidth: 40,
            expandedRowRender: renderSettlementDetails,
            // 旧记录没有快照数据时，不显示展开入口
            rowExpandable: hasSettlementDetails,
          }}
          pagination={{
            current: pageNum,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              t(LOCALS.pagination_display, {
                start: range[0],
                end: range[1],
                total,
              }),
            onChange: (page, size) => {
              setPageNum(page);
              setPageSize(size || 10);
            },
          }}
          scroll={{ x: CASH_SETTLEMENT_HISTORY_TABLE_SCROLL_X }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default CashSettlementHistory;
